import { useCallback, useEffect, useState } from "react";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import EditIcon from "@mui/icons-material/Edit";
import { getCustomerGroupExportSettings } from "../../../services";
import {
  CUSTOMER_GROUP_EXPORT_FORMAT_LABEL,
  CustomerGroupWithExportSettings,
} from "../../../types/customer-group-export.type";
import MtExportSettingsDialog from "./MtExportSettingsDialog";

const NOT_SET = "-";

const COLUMNS = [
  "กลุ่มลูกค้า",
  "รหัส vendor",
  "รูปแบบไฟล์ export",
  "ชื่อไฟล์ (prefix)",
  "หลัก site",
  "Doc type",
  "สาขา VAT",
  "เชน PO",
  "prefix รหัสลูกค้า (PO)",
  "",
];

interface MtExportRowProps {
  group: CustomerGroupWithExportSettings;
  onEdit: (group: CustomerGroupWithExportSettings) => void;
}

function MtExportRow({ group, onEdit }: Readonly<MtExportRowProps>) {
  const handleEdit = () => onEdit(group);
  const formatLabel = group.export_format
    ? CUSTOMER_GROUP_EXPORT_FORMAT_LABEL[group.export_format]
    : NOT_SET;

  return (
    <TableRow hover>
      <TableCell>
        {group.code} ({group.prefix}) — {group.name}
      </TableCell>
      <TableCell>{group.vendor_code ?? NOT_SET}</TableCell>
      <TableCell>{formatLabel}</TableCell>
      <TableCell>{group.export_file_prefix ?? NOT_SET}</TableCell>
      <TableCell>{group.export_site_length ?? NOT_SET}</TableCell>
      <TableCell>{group.export_doc_type ?? NOT_SET}</TableCell>
      <TableCell>{group.export_vat_branch_no ?? NOT_SET}</TableCell>
      <TableCell>{group.po_chain ?? NOT_SET}</TableCell>
      <TableCell>{group.po_customer_code_prefixes?.join(", ") ?? NOT_SET}</TableCell>
      <TableCell align="right">
        <Tooltip title="แก้ไข">
          <IconButton size="small" onClick={handleEdit}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

/**
 * ตั้งค่า export เชน MT (MASTER): each customer group's chain export layout and file settings,
 * and the PO chain whose store numbers resolve against it. Read at export / PO-match time —
 * files already downloaded do not change.
 */
export default function MtExportSettings() {
  const [groups, setGroups] = useState<CustomerGroupWithExportSettings[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editing, setEditing] = useState<CustomerGroupWithExportSettings | null>(null);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getCustomerGroupExportSettings();
      setGroups(data ?? []);
    } catch {
      // Surfaced by the axios interceptor; the table keeps whatever it last showed.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const handleCloseDialog = () => setEditing(null);

  const handleSaved = () => {
    setEditing(null);
    loadGroups();
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        เชนที่ตั้งรูปแบบไฟล์ export ไว้จะแสดงในหน้า รายงาน → การขายเฉพาะกลุ่ม ·
        ค่าที่แก้มีผลกับไฟล์ที่ export หลังบันทึกเท่านั้น
      </Typography>
      <Card sx={{ boxShadow: "none", borderRadius: "12px" }}>
        {loading ? (
          <Stack alignItems="center" sx={{ p: 4 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell key={column} sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                      {column}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <MtExportRow key={group.uuid} group={group} onEdit={setEditing} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>
      <MtExportSettingsDialog
        group={editing}
        groups={groups}
        onClose={handleCloseDialog}
        onSaved={handleSaved}
      />
    </Stack>
  );
}
